import React, { memo, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { TextField, InputControlLabel, Select } from '@picsio/ui';

import localization from '../../shared/strings';
import { useLocalFieldValue, useBlurCommit } from './hooks';
import DetailsBlock from '../DetailsBlock';
import InventoryTextField from './Inventory/InventoryTextField';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import { buildWeightSelectOptions, normalizeWeightUnit, getUnitDisplayLabel } from './helpers';
import buildComplianceFieldLabel from './helpers/buildComplianceFieldLabel';
import {
  normalizeCountryCodeOfOrigin,
  validateCountryCodeOfOrigin,
  normalizeHarmonizedSystemCode,
  validateHarmonizedSystemCode,
} from './helpers/validateComplianceFields';
import AggregatedBooleanField from './aggregated/fields/AggregatedBooleanField';
import AggregatedBoundScalarField from './aggregated/fields/AggregatedBoundScalarField';
import AggregatedMultipleValueHint from './aggregated/fields/AggregatedMultipleValueHint';
import { aggregatedShippingFieldsPropType } from './aggregated/helpers/aggregatedFieldPropTypes';
import { isAggregatedFieldMultiple } from './aggregated/helpers/aggregatedFieldHelpers';
import { resolveAggregatedFieldTooltip } from './aggregated/helpers/resolveAggregatedFieldTooltip';
import s from './styles.module.scss';
import aggregatedFieldStyles from './aggregated/fields/AggregatedBooleanField.module.scss';

const l10n = localization.PRODUCT_INFO;
const inventoryL10n = l10n.inventory;

const validateCountryField = (value) => validateCountryCodeOfOrigin(value, {
  invalidMessage: inventoryL10n.countryCodeOfOriginInvalid,
});
const validateHarmonizedSystemField = (value) => validateHarmonizedSystemCode(value, {
  invalidMessage: inventoryL10n.harmonizedSystemCodeInvalid,
});

const WEIGHT_OPTIONS = buildWeightSelectOptions();

const EMPTY_VALUE = {};

const validateNumeric = (v) => (
  v !== '' && Number.isNaN(Number(v))
    ? { isValid: false, errorMessage: localization.PRODUCT_INFO.validation.enterValidNumber }
    : { isValid: true }
);

const Shipping = ({
  requiresShipping,
  currentValue,
  countryCodeOfOrigin,
  harmonizedSystemCode,
  showComplianceFields,
  hideWeight,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  onChangeRequiresShipping,
  onChangeWeight,
  onChangeCountryCodeOfOrigin,
  onChangeHarmonizedSystemCode,
  readOnly,
  readOnlyTooltip,
  aggregatedFields,
}) => {
  const weightUnit = useMemo(
    () => normalizeWeightUnit(currentValue?.unit),
    [currentValue?.unit],
  );
  // `?? ''` not `|| 0`: seeding 0 made an unset weight indistinguishable from a real zero, and
  // rendered a stored 0 as blank.
  const { localValue, setLocalValue } = useLocalFieldValue(currentValue?.value ?? '');

  const requiresShippingMultiple = isAggregatedFieldMultiple(aggregatedFields?.requiresShipping);
  const weightValueMultiple = isAggregatedFieldMultiple(aggregatedFields?.weightValue);
  const weightUnitMultiple = isAggregatedFieldMultiple(aggregatedFields?.weightUnit);
  const countryMultiple = isAggregatedFieldMultiple(aggregatedFields?.countryCodeOfOrigin);
  const harmonizedMultiple = isAggregatedFieldMultiple(aggregatedFields?.harmonizedSystemCode);

  // Number('') is 0, so an emptied weight used to commit a real zero and could never be removed.
  // The backend accepts `weight: null` as the clear.
  const commitWeight = useCallback((v) => {
    if (weightUnitMultiple) return;
    onChangeWeight(v === '' ? null : { value: Number(v), unit: weightUnit });
  }, [onChangeWeight, weightUnit, weightUnitMultiple]);

  const {
    handleBlur,
    error,
    clearErrorIfValid,
  } = useBlurCommit({
    value: currentValue?.value ?? '',
    localValue,
    setLocalValue,
    onCommit: commitWeight,
    serialize: String,
    validate: validateNumeric,
  });

  const handleChangeRequiresShipping = useCallback(() => {
    if (readOnly) return;
    onChangeRequiresShipping(!requiresShipping);
  }, [requiresShipping, onChangeRequiresShipping, readOnly]);

  const handleChangeWeight = useCallback((event) => {
    if (readOnly) return;
    setLocalValue(event.target.value);
    clearErrorIfValid(event.target.value);
  }, [readOnly, setLocalValue, clearErrorIfValid]);

  const saveWeight = useCallback(() => {
    if (readOnly) return;
    handleBlur();
  }, [readOnly, handleBlur]);

  const handleChangeUnit = useCallback((event) => {
    if (readOnly) return;
    if (weightValueMultiple) return;
    // Changing the unit must not resurrect a cleared weight as 0.
    if (localValue === '' || localValue === null) return;
    onChangeWeight({ value: Number(localValue), unit: event.target.value });
  }, [localValue, onChangeWeight, readOnly, weightValueMultiple]);

  const renderUnitValue = useCallback((value) => {
    const normalizedUnit = normalizeWeightUnit(value);
    const item = WEIGHT_OPTIONS.find(({ id }) => id === normalizedUnit);
    return item?.value || getUnitDisplayLabel('weight', normalizedUnit);
  }, []);

  const defaultWeightUnit = WEIGHT_OPTIONS[0]?.id || normalizeWeightUnit();

  const showWeightRow = !hideWeight && (
    aggregatedFields
      ? (requiresShipping || requiresShippingMultiple || weightValueMultiple || weightUnitMultiple)
      : requiresShipping
  );

  const physicalProductLabel = buildComplianceFieldLabel({
    label: l10n.physicalProduct,
    diff: diff?.requiresShipping,
    dimLabel: readOnly,
  });

  return (
    <DetailsBlock
      dataQa="productShipping"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <Choose>
        <When condition={requiresShippingMultiple}>
          <AggregatedBooleanField
            fieldKey="requiresShipping"
            field={aggregatedFields.requiresShipping}
            label={physicalProductLabel}
            dialogLabel={l10n.physicalProduct}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
        </When>
        <Otherwise>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <div className={s.row}>
              <InputControlLabel
                control="checkbox"
                label={physicalProductLabel}
                value={requiresShipping}
                disabled={readOnly}
                onChange={handleChangeRequiresShipping}
              />
            </div>
          </ReadOnlyFieldTooltip>
        </Otherwise>
      </Choose>
      <If condition={showWeightRow}>
        <div className={s.row}>
          <Choose>
            <When condition={weightValueMultiple}>
              <AggregatedBoundScalarField
                fieldKey="weight"
                field={aggregatedFields.weightValue}
                label={<Tooltip content={diff?.weight ? 'Unpublished changes' : null} placement="top"><span className={diff?.weight ? s.diffDot : undefined}>{l10n.autoMappedFieldLabels.weight}</span></Tooltip>}
                readOnly={readOnly || weightUnitMultiple}
                readOnlyTooltip={readOnlyTooltip}
                mapCommitValue={(value) => (
                  value === '' || value == null
                    ? null
                    : { value: Number(value), unit: weightUnit }
                )}
              />
            </When>
            <Otherwise>
              <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
                <TextField
                  type="number"
                  value={localValue}
                  label={<Tooltip content={diff?.weight ? 'Unpublished changes' : null} placement="top"><span className={diff?.weight ? s.diffDot : undefined}>{l10n.autoMappedFieldLabels.weight}</span></Tooltip>}
                  disabled={readOnly}
                  error={Boolean(error)}
                  helperText={error}
                  inputProps={{ min: 0, step: 'any' }}
                  onChange={handleChangeWeight}
                  onBlur={saveWeight}
                />
              </ReadOnlyFieldTooltip>
            </Otherwise>
          </Choose>
          <Choose>
            <When condition={weightUnitMultiple}>
              <div className={aggregatedFieldStyles.unitRow}>
                <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
                  <Select
                    style={{ width: 50, height: 30 }}
                    value={defaultWeightUnit}
                    options={WEIGHT_OPTIONS}
                    renderValue={renderUnitValue}
                    dataId="id"
                    dataValue="value"
                    disabled={readOnly || weightValueMultiple}
                    onChange={readOnly || weightValueMultiple ? undefined : handleChangeUnit}
                  />
                </ReadOnlyFieldTooltip>
                <AggregatedMultipleValueHint />
              </div>
            </When>
            <Otherwise>
              <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
                <Select
                  style={{ width: 50, height: 30 }}
                  value={weightUnit}
                  options={WEIGHT_OPTIONS}
                  renderValue={renderUnitValue}
                  dataId="id"
                  dataValue="value"
                  disabled={readOnly}
                  onChange={handleChangeUnit}
                />
              </ReadOnlyFieldTooltip>
            </Otherwise>
          </Choose>
        </div>
      </If>
      <If condition={showComplianceFields}>
        <div className={s.hr} />
        <div className={s.row}>
          <Choose>
            <When condition={countryMultiple}>
              <AggregatedBoundScalarField
                fieldKey="countryCodeOfOrigin"
                field={aggregatedFields.countryCodeOfOrigin}
                label={buildComplianceFieldLabel({
                  label: inventoryL10n.countryCodeOfOrigin,
                  diff: diff?.countryCodeOfOrigin,
                  infoTooltip: inventoryL10n.countryCodeOfOriginTooltip,
                })}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <InventoryTextField
                value={countryCodeOfOrigin || ''}
                label={buildComplianceFieldLabel({
                  label: inventoryL10n.countryCodeOfOrigin,
                  diff: diff?.countryCodeOfOrigin,
                  infoTooltip: inventoryL10n.countryCodeOfOriginTooltip,
                })}
                placeholder={inventoryL10n.countryCodeOfOriginPlaceholder}
                normalize={normalizeCountryCodeOfOrigin}
                validate={validateCountryField}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.countryCodeOfOrigin,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                onCommit={onChangeCountryCodeOfOrigin}
              />
            </Otherwise>
          </Choose>
          <Choose>
            <When condition={harmonizedMultiple}>
              <AggregatedBoundScalarField
                fieldKey="harmonizedSystemCode"
                field={aggregatedFields.harmonizedSystemCode}
                label={buildComplianceFieldLabel({
                  label: inventoryL10n.harmonizedSystemCode,
                  diff: diff?.harmonizedSystemCode,
                  infoTooltip: inventoryL10n.harmonizedSystemCodeTooltip,
                })}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <InventoryTextField
                value={harmonizedSystemCode || ''}
                label={buildComplianceFieldLabel({
                  label: inventoryL10n.harmonizedSystemCode,
                  diff: diff?.harmonizedSystemCode,
                  infoTooltip: inventoryL10n.harmonizedSystemCodeTooltip,
                })}
                placeholder={inventoryL10n.harmonizedSystemCodePlaceholder}
                normalize={normalizeHarmonizedSystemCode}
                validate={validateHarmonizedSystemField}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.harmonizedSystemCode,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                onCommit={onChangeHarmonizedSystemCode}
              />
            </Otherwise>
          </Choose>
        </div>
      </If>
    </DetailsBlock>
  );
};

Shipping.propTypes = {
  requiresShipping: PropTypes.bool,
  currentValue: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
  }),
  countryCodeOfOrigin: PropTypes.string,
  harmonizedSystemCode: PropTypes.string,
  showComplianceFields: PropTypes.bool,
  hideWeight: PropTypes.bool,
  diff: PropTypes.shape({
    weight: PropTypes.bool,
    requiresShipping: PropTypes.bool,
    countryCodeOfOrigin: PropTypes.bool,
    harmonizedSystemCode: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  aggregatedFields: aggregatedShippingFieldsPropType,
  onChangeRequiresShipping: PropTypes.func.isRequired,
  onChangeWeight: PropTypes.func.isRequired,
  onChangeCountryCodeOfOrigin: PropTypes.func,
  onChangeHarmonizedSystemCode: PropTypes.func,
};

Shipping.defaultProps = {
  requiresShipping: false,
  currentValue: EMPTY_VALUE,
  countryCodeOfOrigin: '',
  harmonizedSystemCode: '',
  showComplianceFields: false,
  hideWeight: false,
  diff: {},
  readOnly: false,
  readOnlyTooltip: null,
  onChangeCountryCodeOfOrigin: () => null,
  onChangeHarmonizedSystemCode: () => null,
  aggregatedFields: null,
};

export default memo(Shipping);
